/* =============================================================================
   SJ Seasonal product page
   - Santa flight that reveals the illustrated headline (once, on load)
   - Layered CSS snowfall (paused offscreen, off for reduced motion)
   - Purchase panel: one-time / duo, type pills, quantity, AJAX add to cart
   - Scroll reveals for this page and the reused SJ sections
   Loaded with `defer` by sj-xmas-hero and sj-xmas-benefits; runs once.
   ============================================================================= */
(function () {
  'use strict';
  if (window.__sjxLoaded) return;
  window.__sjxLoaded = true;

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduced() { return reduceMQ.matches; }
  function rand(a, b) { return a + Math.random() * (b - a); }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
  var EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

  // ── Scroll reveal ─────────────────────────────────────────────────────────
  // .sj-reveal belongs to the reused SJ sections (guarantee, FAQ, more
  // products); their usual hero isn't on this page, so it's wired here.
  // [data-sjx-rise] are this page's own cards: hidden only once armed.
  function initReveal() {
    var reveals = document.querySelectorAll('.sj-landing .sj-reveal');
    var rises = document.querySelectorAll('.sjx [data-sjx-rise]');
    if (!('IntersectionObserver' in window)) {
      reveals.forEach(function (el) { el.classList.add('sj-reveal--visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sj-reveal--visible', 'is-in');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    reveals.forEach(function (el) { io.observe(el); });
    rises.forEach(function (el) { el.classList.add('is-armed'); io.observe(el); });
  }

  // ── Snow ──────────────────────────────────────────────────────────────────
  // Each layer is a handful of absolutely positioned spans driven by two CSS
  // animations (fall + sway), so it runs off the main thread. Negative delays
  // start every flake mid-fall so the scene never "begins" snowing.
  var SNOW = {
    far:    { mobile: 14, desktop: 24, size: [2, 3.5], dur: [16, 24], o: [0.35, 0.6],  dx: 18, amp: [2, 5],  crystal: 0 },
    mid:    { mobile: 10, desktop: 18, size: [3, 5],   dur: [10, 15], o: [0.6, 0.85],  dx: 26, amp: [4, 8],  crystal: 0.18 },
    near:   { mobile: 4,  desktop: 7,  size: [7, 11],  dur: [7, 10],  o: [0.75, 0.95], dx: 34, amp: [6, 12], crystal: 0.5 },
    sparse: { mobile: 9,  desktop: 18, size: [3, 6],   dur: [13, 20], o: [0.75, 0.95], dx: 24, amp: [4, 9],  crystal: 0.22 },
    // Large pale crystals drifting very slowly behind the benefits content
    deco:   { mobile: 7,  desktop: 12, size: [9, 22],  dur: [40, 64], o: [0.55, 0.9],  dx: 40, amp: [6, 14], crystal: 1, cls: 'is-deco' }
  };

  function initSnow() {
    if (reduced()) return;
    var wide = window.matchMedia('(min-width: 860px)').matches;
    var layers = document.querySelectorAll('.sjx [data-sjx-snow]');
    if (!layers.length) return;

    var ro = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
      entries.forEach(function (e) { setFall(e.target); });
    }) : null;
    var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.target.classList.toggle('is-paused', !e.isIntersecting); });
    }) : null;

    function setFall(layer) {
      layer.style.setProperty('--fall', (layer.clientHeight + 30) + 'px');
    }

    layers.forEach(function (layer) {
      var cfg = SNOW[layer.getAttribute('data-sjx-snow')];
      if (!cfg) return;
      var n = wide ? cfg.desktop : cfg.mobile;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < n; i++) {
        var crystal = Math.random() < cfg.crystal;
        var s = rand(cfg.size[0], cfg.size[1]) * (crystal ? 1.9 : 1);
        var d = rand(cfg.dur[0], cfg.dur[1]);
        var flake = document.createElement('span');
        flake.className = 'sjx-flake' + (crystal ? ' is-crystal' : '') + (cfg.cls ? ' ' + cfg.cls : '');
        flake.style.cssText =
          '--x:' + rand(0, 100).toFixed(2) + '%;' +
          '--s:' + s.toFixed(1) + 'px;' +
          '--d:' + d.toFixed(2) + 's;' +
          '--delay:' + (-rand(0, d)).toFixed(2) + 's;' +
          '--dx:' + rand(-cfg.dx, cfg.dx).toFixed(1) + 'px;' +
          '--sw:' + rand(2.6, 4.6).toFixed(2) + 's;' +
          '--amp:' + rand(cfg.amp[0], cfg.amp[1]).toFixed(1) + 'px;' +
          '--o:' + rand(cfg.o[0], cfg.o[1]).toFixed(2);
        flake.appendChild(document.createElement('i'));
        frag.appendChild(flake);
      }
      layer.appendChild(frag);
      setFall(layer);
      if (ro) ro.observe(layer);
      if (io) io.observe(layer);
    });

    // Steam on the benefits mug pauses offscreen too
    if (io) {
      var stIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.target.classList.toggle('is-offscreen', !e.isIntersecting); });
      });
      document.querySelectorAll('.sjx-benefits').forEach(function (el) { stIO.observe(el); });
    }
  }

  // ── Santa flight + headline reveal ────────────────────────────────────────
  // Progressive enhancement: the headline is visible in the HTML. The mask is
  // applied in the same frame the flight starts, and only if the artwork has
  // decoded, the hero is on screen, and motion is allowed. Otherwise nothing
  // happens and the static scene stands.
  function decodeImg(img) {
    if (!img) return Promise.resolve();
    if (img.complete && img.naturalWidth) {
      return img.decode ? img.decode().catch(function () {}) : Promise.resolve();
    }
    return new Promise(function (resolve) {
      img.addEventListener('load', function () {
        (img.decode ? img.decode() : Promise.resolve()).catch(function () {}).then(resolve);
      }, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  function initSanta(root) {
    var scene = root.querySelector('[data-sjx-scene]');
    var santa = root.querySelector('[data-sjx-santa]');
    var head = root.querySelector('[data-sjx-headline]');
    // Show the static headline (used whenever the intro doesn't play).
    function showStatic() {
      head.classList.add('is-live');
      document.documentElement.classList.remove('sjx-intro');
    }
    if (!scene || !santa || !head || reduced() || !santa.animate) { if (head) showStatic(); return; }

    var gaveUp = false;
    // A slow connection shouldn't produce a late, distracting intro.
    var giveUpTimer = setTimeout(function () { gaveUp = true; showStatic(); }, 3500);

    // Back/Forward restores this page from the browser cache with the intro
    // already finished. Replay it from a clean state.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted || reduced()) return;
      santa.getAnimations().forEach(function (a) { a.cancel(); });
      scene.querySelectorAll('.sjx-spark').forEach(function (n) { n.remove(); });
      var old = head.querySelector('.sjx-letters');
      if (old) old.remove();
      if (letters) letters.wrap.querySelectorAll('*').forEach(function (n) {
        n.getAnimations().forEach(function (a) { a.cancel(); });
      });
      var img = head.querySelector('img');
      if (img) img.style.opacity = '';
      head.classList.remove('is-live', 'is-revealing');
      requestAnimationFrame(function () { requestAnimationFrame(tryFly); });
    });

    var letters = null; // built only if the per-letter art loads in time
    Promise.all([
      decodeImg(santa.querySelector('img')),
      decodeImg(head.querySelector('img')),
      loadLetters().then(function (l) { letters = l; }, function () {})
    ]).then(function () {
      if (gaveUp) return;
      clearTimeout(giveUpTimer);
      if (document.visibilityState === 'hidden') {
        // Opened in a background tab: fly when the visitor actually arrives.
        document.addEventListener('visibilitychange', function onVis() {
          if (document.visibilityState !== 'visible') return;
          document.removeEventListener('visibilitychange', onVis);
          requestAnimationFrame(tryFly);
        });
      } else {
        requestAnimationFrame(tryFly);
      }
    });

    // Fetch the letter layout and decode every letter before committing to
    // the per-letter reveal. Any failure falls back to the soft wipe.
    function loadLetters() {
      var url = head.getAttribute('data-sjx-letters');
      if (!url || !window.fetch) return Promise.reject();
      var base = url.slice(0, url.lastIndexOf('/') + 1);
      var query = url.indexOf('?') > -1 ? url.slice(url.indexOf('?')) : '';
      return fetch(url).then(function (r) {
        if (!r.ok) throw new Error('letters');
        return r.json();
      }).then(function (layout) {
        var wrap = document.createElement('span');
        wrap.className = 'sjx-letters';
        wrap.setAttribute('aria-hidden', 'true');
        var els = layout.letters.map(function (l, i) {
          var src = base + l.f;
          var box = document.createElement('span');
          box.className = 'sjx-letter';
          box.style.cssText = 'left:' + l.x + '%;top:' + l.y + '%;width:' + l.w + '%;';
          var img = document.createElement('img');
          img.alt = '';
          img.decoding = 'async';
          img.src = src;
          // Colour layer cut to the letter's own shape; multiplying keeps the
          // artwork's outline and shading, so it reads as a red/green letter.
          var tint = document.createElement('span');
          tint.className = 'sjx-letter__tint ' + (i % 2 ? 'is-green' : 'is-red');
          tint.style.webkitMaskImage = tint.style.maskImage = 'url("' + src + '")';
          box.appendChild(img);
          box.appendChild(tint);
          wrap.appendChild(box);
          return { el: box, img: img, tint: tint, cx: (l.x + l.w / 2) / 100, line: l.line };
        });
        return Promise.all(els.map(function (o) { return decodeImg(o.img); })).then(function () {
          return { wrap: wrap, items: els };
        });
      });
    }

    function tryFly() {
      var r = scene.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // Skip if the headline area is already scrolled out of view.
      if (r.top + r.height * 0.35 < 0 || r.top > vh * 0.85) { showStatic(); return; }
      fly();
    }

    function fly() {
      var W = scene.clientWidth;
      var H = scene.clientHeight;
      var sw = santa.offsetWidth;
      var sh = santa.offsetHeight;
      var sr = scene.getBoundingClientRect();
      var hr = head.getBoundingClientRect();
      var hx = hr.left - sr.left;
      var hw = hr.width || 1;

      var D = 3000;
      var x0 = -sw - 8;
      var x1 = W + 24;
      // Symmetric arc: enters low-left, crests when the sleigh is centred on
      // screen, then descends to leave at the same height it entered.
      var yStart = H * 0.30;
      var yApex = H * 0.07 - sh * 0.125;

      function ease(t) {
        // Mostly linear with a gentle ease in/out: reads as gliding, not
        // accelerating, and keeps the reveal speed even across the words.
        return 0.6 * t + 0.4 * (0.5 - 0.5 * Math.cos(Math.PI * t));
      }
      // Fraction of the horizontal travel at which the sleigh is centred
      var uApex = clamp((W / 2 - sw / 2 - x0) / (x1 - x0), 0.2, 0.8);
      function pos(t) {
        var u = ease(t);
        var k = (u - uApex) / (u < uApex ? uApex : 1 - uApex);
        return {
          x: x0 + (x1 - x0) * u,
          y: yApex + (yStart - yApex) * k * k
        };
      }

      var frames = [];
      var N = 72;
      for (var i = 0; i <= N; i++) {
        var t = i / N;
        var p = pos(t);
        var q = pos(Math.min(1, t + 0.01));
        var pr = pos(Math.max(0, t - 0.01));
        var ang = Math.atan2(q.y - pr.y, (q.x - pr.x) || 1) * 180 / Math.PI;
        // Climbing: a restrained nose-up tilt. Descending: the body follows
        // the path exactly, so the lead reindeer is always the lowest point
        // and the sleigh never drops faster than the team pulling it.
        ang = ang < 0 ? Math.max(ang * 0.7, -9) : ang;
        frames.push({
          offset: t,
          transform: 'translate3d(' + p.x.toFixed(1) + 'px,' + p.y.toFixed(1) + 'px,0) rotate(' + ang.toFixed(2) + 'deg)',
          opacity: 1
        });
      }

      // The reveal edge trails just behind the sleigh, so the words appear
      // as Santa passes over them.
      // Mask image is 3x the headline width with a soft edge at its centre;
      // positions stay inside 12.5–87.5% so the mask always fully covers the
      // headline (no bare strip at either end of the flight).
      function maskAt(t) {
        var edge = pos(t).x + sw * 0.22 - hx;
        edge = clamp(edge, -0.25 * hw, 1.25 * hw);
        return (((1.5 * hw - edge) / (2 * hw)) * 100).toFixed(2) + '% 0%';
      }

      var anim;
      head.classList.add('is-live');
      if (letters) {
        anim = santa.animate(frames, { duration: D, easing: 'linear', fill: 'none' });
        popLetters(pos, sw, D);
        sparkleTrail(pos, sw, sh, D);
        return;
      }

      head.classList.add('is-revealing');
      head.style.webkitMaskPosition = maskAt(0);
      head.style.maskPosition = maskAt(0);

      anim = santa.animate(frames, { duration: D, easing: 'linear', fill: 'none' });

      // The mask follows the sleigh's actual clock, so it can never drift
      // out of sync even if the main thread stutters.
      var raf;
      function tick() {
        var ct = anim.currentTime || 0;
        var t = clamp(ct / D, 0, 1);
        var m = maskAt(t);
        head.style.webkitMaskPosition = m;
        head.style.maskPosition = m;
        if (t < 1 && anim.playState !== 'finished') raf = requestAnimationFrame(tick);
      }
      raf = requestAnimationFrame(tick);

      sparkleTrail(pos, sw, sh, D);

      function done() {
        cancelAnimationFrame(raf);
        head.classList.remove('is-revealing');
        head.style.webkitMaskPosition = '';
        head.style.maskPosition = '';
      }
      anim.onfinish = done;
      anim.oncancel = done;
    }

    // Letter-by-letter: each letter drops in with a springy bounce the moment
    // the sleigh passes over it, lands in candy-cane red or green, then
    // settles to the artwork's white. The full headline image is swapped back
    // in at the end (pixel-identical), so the resting state is the original.
    function popLetters(pos, sw, D) {
      var sr = scene.getBoundingClientRect();
      var hr = head.getBoundingClientRect();
      var hx = hr.left - sr.left;
      var hw = hr.width || 1;
      var base = head.querySelector('img');
      head.appendChild(letters.wrap);
      if (base) base.style.opacity = '0';

      // When does the reveal point (just behind the sleigh) reach x?
      function timeAt(x) {
        for (var k = 0; k <= 200; k++) {
          var t = k / 200;
          if (pos(t).x + sw * 0.22 - hx >= x) return t;
        }
        return 1;
      }

      var last = 0;

      letters.items.forEach(function (o, i) {
        var delay = timeAt(o.cx * hw) * D + o.line * 90;
        last = Math.max(last, delay);
        var tilt = (i % 2 ? 1 : -1) * 10;
        o.el.animate([
          { opacity: 0, transform: 'translateY(-70%) scale(0.45) rotate(' + tilt + 'deg)' },
          { opacity: 1, transform: 'translateY(8%) scale(1.14, 0.9) rotate(' + (-tilt * 0.3) + 'deg)', offset: 0.38 },
          { transform: 'translateY(-6%) scale(0.95, 1.06) rotate(0deg)', offset: 0.58 },
          { transform: 'translateY(2%) scale(1.03, 0.98)', offset: 0.78 },
          { opacity: 1, transform: 'none' }
        ], { duration: 640, delay: delay, easing: 'cubic-bezier(0.33, 1, 0.68, 1)', fill: 'both' });
        // Hold the colour while it lands, then fade straight to white.
        o.tint.animate([
          { opacity: 1 },
          { opacity: 1, offset: 0.4 },
          { opacity: 0 }
        ], { duration: 1500, delay: delay, easing: 'ease-in-out', fill: 'both' });
        twinkle(o, delay + 230, hx, hw);
      });

      // Restore the single original image once everything has settled.
      setTimeout(function () {
        if (base) base.style.opacity = '';
        requestAnimationFrame(function () {
          if (letters.wrap.parentNode) letters.wrap.parentNode.removeChild(letters.wrap);
        });
      }, last + 1600);
    }

    // Two tiny glints as a letter lands.
    function twinkle(o, delay, hx, hw) {
      var sr = scene.getBoundingClientRect();
      var lr = o.el.getBoundingClientRect();
      for (var k = 0; k < 2; k++) {
        var el = document.createElement('span');
        el.className = 'sjx-spark';
        el.setAttribute('aria-hidden', 'true');
        var size = rand(5, 9);
        el.style.cssText =
          'left:' + (lr.left - sr.left + lr.width * rand(0.1, 0.9)).toFixed(1) + 'px;' +
          'top:' + (lr.top - sr.top + lr.height * rand(0.05, 0.45)).toFixed(1) + 'px;' +
          'width:' + size.toFixed(1) + 'px;height:' + size.toFixed(1) + 'px;z-index:10;';
        scene.appendChild(el);
        var a = el.animate([
          { opacity: 0, transform: 'scale(0.3) rotate(0deg)' },
          { opacity: 1, transform: 'scale(1.1) rotate(45deg)', offset: 0.35 },
          { opacity: 0, transform: 'scale(0.4) rotate(90deg)' }
        ], { duration: 700, delay: delay + k * 120, easing: 'ease-out', fill: 'both' });
        a.onfinish = (function (node) { return function () { node.remove(); }; })(el);
      }
    }

    // A few faint twinkles left behind the runners. Restrained on purpose.
    function sparkleTrail(pos, sw, sh, D) {
      var COUNT = 7;
      for (var i = 0; i < COUNT; i++) {
        (function (i) {
          var t = 0.18 + (i / (COUNT - 1)) * 0.6;
          var p = pos(t);
          var el = document.createElement('span');
          el.className = 'sjx-spark';
          el.setAttribute('aria-hidden', 'true');
          var size = rand(5, 8);
          el.style.cssText =
            'left:' + (p.x + sw * rand(0.04, 0.14)).toFixed(1) + 'px;' +
            'top:' + (p.y + sh * rand(0.72, 0.92)).toFixed(1) + 'px;' +
            'width:' + size.toFixed(1) + 'px;height:' + size.toFixed(1) + 'px;';
          scene.appendChild(el);
          var a = el.animate([
            { opacity: 0, transform: 'scale(0.4)' },
            { opacity: 0.9, transform: 'scale(1)', offset: 0.3 },
            { opacity: 0, transform: 'translateY(8px) scale(0.6)' }
          ], { duration: 800, delay: t * D + 60, easing: 'ease-out', fill: 'both' });
          a.onfinish = function () { el.remove(); };
        })(i);
      }
    }
  }

  // ── Purchase panel ────────────────────────────────────────────────────────
  function initBuy(form) {
    var dataEl = form.querySelector('[data-sjx-data]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var str = data.strings || {};
    var groups = { single: data.primary, duoA: data.primary, duoB: data.companion };
    var atc = form.querySelector('[data-sjx-atc]');
    var atcLabel = form.querySelector('[data-sjx-atc-label]');
    var errEl = form.querySelector('[data-sjx-error]');
    var statusEl = form.querySelector('[data-sjx-status]');
    var qtyInput = form.querySelector('[data-sjx-qty-input]');
    var qtyBtns = form.querySelectorAll('[data-sjx-qty]');
    var singlePriceEl = form.querySelector('[data-sjx-single-price]');
    var duoSaleEl = form.querySelector('[data-sjx-duo-sale]');
    var duoRegEl = form.querySelector('[data-sjx-duo-reg]');
    var cards = form.querySelectorAll('[data-sjx-opt]');
    var mode = 'single';
    var busy = false;
    var doneTimer;

    function pickers(group) {
      return form.querySelectorAll('.sjx-pick[data-group="' + group + '"]');
    }
    function chosen(group) {
      var sel = {};
      pickers(group).forEach(function (fs) {
        var c = fs.querySelector('input:checked');
        if (c) sel[fs.getAttribute('data-option-index')] = c.value;
      });
      return sel;
    }
    function matches(v, sel) {
      return Object.keys(sel).every(function (i) { return v.options[i] === sel[i]; });
    }
    function resolve(group) {
      var vs = groups[group];
      if (!vs) return null;
      var sel = chosen(group);
      for (var i = 0; i < vs.length; i++) if (matches(vs[i], sel)) return vs[i];
      return null;
    }

    // Flag values that can't be bought with the other current choices.
    function markAvailability(group) {
      var vs = groups[group];
      if (!vs) return;
      var sel = chosen(group);
      pickers(group).forEach(function (fs) {
        var idx = fs.getAttribute('data-option-index');
        fs.querySelectorAll('.sjx-pill').forEach(function (pill) {
          var input = pill.querySelector('input');
          var trial = Object.assign({}, sel);
          trial[idx] = input.value;
          var ok = vs.some(function (v) { return v.available && matches(v, trial); });
          pill.classList.toggle('is-unavailable', !ok);
          input.setAttribute('aria-label', input.value + (ok ? '' : ' (sold out)'));
        });
      });
    }

    function qty() {
      var n = parseInt(qtyInput && qtyInput.value, 10);
      return isNaN(n) ? 1 : n;
    }
    function qtyMax() {
      var m = qtyInput && parseInt(qtyInput.getAttribute('max'), 10);
      return m > 0 ? m : Infinity;
    }
    function setQty(n, dir) {
      if (!qtyInput) return;
      var next = clamp(Math.round(n), 1, qtyMax());
      var changed = next !== qty();
      qtyInput.value = next;
      qtyBtns.forEach(function (b) {
        var d = parseInt(b.getAttribute('data-sjx-qty'), 10);
        b.disabled = d < 0 ? next <= 1 : next >= qtyMax();
      });
      if (changed && dir && !reduced() && qtyInput.animate) {
        qtyInput.animate([
          { transform: 'translateY(' + (dir > 0 ? 40 : -40) + '%)', opacity: 0 },
          { transform: 'none', opacity: 1 }
        ], { duration: 220, easing: EASE_OUT });
      }
    }

    function currentItems() {
      if (mode === 'duo') {
        var a = resolve('duoA');
        var b = resolve('duoB');
        if (!a || !b || !a.available || !b.available) return null;
        return [{ id: a.id, quantity: 1 }, { id: b.id, quantity: 1 }];
      }
      var v = resolve('single');
      if (!v || !v.available) return null;
      return [{ id: v.id, quantity: qty() }];
    }

    function refreshAtc() {
      if (busy || !atc) return;
      var ok = !!currentItems();
      atc.disabled = !ok;
      atc.classList.toggle('is-soldout', !ok);
      atc.classList.remove('is-done');
      atcLabel.textContent = ok ? str.add : str.soldOut;
    }

    function update() {
      ['single', 'duoA', 'duoB'].forEach(markAvailability);
      var v = resolve('single');
      if (v && singlePriceEl) singlePriceEl.textContent = v.priceFmt;
      if (qtyInput) {
        if (v && v.max > 0) qtyInput.setAttribute('max', v.max);
        else qtyInput.removeAttribute('max');
        setQty(qty());
      }
      if (groups.duoB) {
        var a = resolve('duoA');
        var b = resolve('duoB');
        var pr = a && b && data.duoPrices[a.id + ':' + b.id];
        if (pr) {
          if (duoSaleEl) duoSaleEl.textContent = pr.sale;
          if (duoRegEl) duoRegEl.textContent = pr.reg;
        }
      }
      refreshAtc();
    }

    function hideError() {
      if (!errEl) return;
      errEl.hidden = true;
      errEl.textContent = '';
    }
    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg || str.error;
      errEl.hidden = false;
      if (!reduced()) {
        atc.classList.remove('is-shake');
        void atc.offsetWidth; // restart the shake on repeat failures
        atc.classList.add('is-shake');
      }
    }

    function setMode(next) {
      mode = next;
      cards.forEach(function (card) {
        var on = card.getAttribute('data-sjx-opt') === mode;
        card.classList.toggle('is-selected', on);
        var body = card.querySelector('[data-sjx-body]');
        if (body) body.inert = !on;
      });
      hideError();
      refreshAtc();
    }

    // Holly Jolly appears in both the single and duo pickers; keep its type
    // in step so switching cards never silently changes what you picked.
    function mirror(fromGroup, input) {
      var toGroup = fromGroup === 'single' ? 'duoA' : fromGroup === 'duoA' ? 'single' : null;
      if (!toGroup) return;
      var idx = input.closest('.sjx-pick').getAttribute('data-option-index');
      var target = form.querySelector('.sjx-pick[data-group="' + toGroup + '"][data-option-index="' + idx + '"]');
      if (!target) return;
      target.querySelectorAll('input').forEach(function (i) { i.checked = i.value === input.value; });
    }

    form.addEventListener('change', function (e) {
      var t = e.target;
      if (t.classList.contains('sjx-opt__radio')) {
        setMode(t.value);
        return;
      }
      var pick = t.closest('.sjx-pick');
      if (pick) {
        mirror(pick.getAttribute('data-group'), t);
        hideError();
        update();
        return;
      }
      if (t === qtyInput) {
        setQty(qty());
        refreshAtc();
      }
    });

    qtyBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var d = parseInt(btn.getAttribute('data-sjx-qty'), 10);
        setQty(qty() + d, d);
        hideError();
        refreshAtc();
      });
    });

    if (qtyInput) {
      qtyInput.addEventListener('blur', function () { setQty(qty()); });
    }

    function postJSON(url, body) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          if (!r.ok || j.status) {
            var err = new Error(j.description || j.message || str.error);
            throw err;
          }
          return j;
        });
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;
      var items = currentItems();
      if (!items) {
        showError(str.soldOut);
        return;
      }

      busy = true;
      clearTimeout(doneTimer);
      hideError();
      atc.classList.remove('is-done', 'is-shake');
      atc.classList.add('is-busy');
      atc.setAttribute('aria-busy', 'true');

      var routes = (window.theme && window.theme.routes) || {};
      var addUrl = (routes.cart_add_url || '/cart/add') + '.js';
      var updateUrl = (routes.cart_update_url || '/cart/update') + '.js';
      var modal = window.theme && window.theme.settings && window.theme.settings.cartType === 'modal';
      var sections = 'cart-items,cart-footer,cart-item-count';
      var body = { items: items };
      if (modal) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      postJSON(addUrl, body)
        .then(function (res) {
          // Optional: only when the store's duo discount is a code rather
          // than automatic. Shopify still decides whether it applies.
          if (mode !== 'duo' || !data.discountCode) return res;
          var upd = { discount: data.discountCode };
          if (modal) { upd.sections = sections; upd.sections_url = window.location.pathname; }
          return postJSON(updateUrl, upd)
            .then(function (cart) { return Object.assign({}, res, { sections: cart.sections || res.sections }); })
            .catch(function () { return res; });
        })
        .then(function (res) {
          busy = false;
          atc.classList.remove('is-busy');
          atc.removeAttribute('aria-busy');
          if (statusEl) statusEl.textContent = str.added;

          if (!modal) {
            window.location.href = routes.cart_url || '/cart';
            return;
          }

          var first = (res.items && res.items[0]) || {};
          document.body.dispatchEvent(new CustomEvent('shapes:cart:afteradditem', {
            bubbles: true,
            detail: {
              response: Object.assign({}, res, { key: first.key, variant_id: first.variant_id }),
              sourceId: form.id
            }
          }));

          // Brief, unmissable confirmation, then back to normal.
          atcLabel.textContent = str.added;
          atc.style.setProperty('--sjx-done-shift', (atcLabel.offsetWidth / 2 + 4) + 'px');
          atc.classList.add('is-done');
          doneTimer = setTimeout(function () {
            atc.classList.remove('is-done');
            if (statusEl) statusEl.textContent = '';
            refreshAtc();
          }, 1800);
        })
        .catch(function (err) {
          busy = false;
          atc.classList.remove('is-busy');
          atc.removeAttribute('aria-busy');
          refreshAtc();
          // Network failures surface as TypeError ("Failed to fetch"); show
          // our own wording. Shopify's messages (e.g. stock limits) pass through.
          showError(err && !(err instanceof TypeError) ? err.message : str.error);
        });
    });

    // Browsers can restore radio state on reload or Back/Forward while our
    // cards show the default. Always start from what's actually checked, so
    // tapping the other card is a real change.
    function syncFromDom() {
      var checked = form.querySelector('.sjx-opt__radio:checked');
      setMode(checked ? checked.value : 'single');
      update();
    }
    syncFromDom();
    window.addEventListener('pageshow', function (e) { if (e.persisted) syncFromDom(); });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  function boot() {
    document.querySelectorAll('.sjx[data-sjx-hero]').forEach(initSanta);
    document.querySelectorAll('form[data-sjx-buy]').forEach(initBuy);
    initSnow();
    initReveal();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
